import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  TablePagination,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link, useSearchParams } from "react-router-dom";
import PageHeader1 from "../../components/common/PageHeader1";
import {
  fetchAstroTickets,
  deleteAstroTicket,
} from "../../Redux/Reducers/AstroTicketReducer";
import socket from "../../socket/socket";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { convertISTToUTCForAPI, formatUTCDate } from '../../utils/dateUtils';
import { getIntParam, getStringParam, setIfTruthy } from '../../utils/urlState';

function AstrologerSupportList() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const didInitUrlSyncRef = useRef(false);
  const applySawLoadingRef = useRef(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: getIntParam(searchParams, "page", 0),
    pageSize: getIntParam(searchParams, "pageSize", 10),
  });

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayDate = getTodayDate();

  const [searchQuery, setSearchQuery] = useState(getStringParam(searchParams, "search", ""));
  const [appliedSearchQuery, setAppliedSearchQuery] = useState(getStringParam(searchParams, "search", ""));
  const [startDate, setStartDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [subject, setSubject] = useState(getStringParam(searchParams, "subject", ""));
  const [appliedSubject, setAppliedSubject] = useState(getStringParam(searchParams, "subject", ""));
  const [lastAction, setLastAction] = useState(null); // "apply" to track when Apply Filters was clicked
  const [isExporting, setIsExporting] = useState(false);
  
  const subjectOptions = [
    'Payment issue',
      'Chat related issue',
      'Profile issue',
      'Notification issue',
      'Suggestion',
      'Others',
    'old'
  ];
  const isInitialMount = useRef(true);
  const lastParamsRef = useRef(null);

  const pagination = useSelector((state) => state?.AstroTicketReducer?.pagination) || {};
  const DataAstro = useSelector((state) => state.AstroTicketReducer?.data || []);
  const ticketsLoading = useSelector((state) => state?.AstroTicketReducer?.status === 'loading');
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(getStringParam(searchParams, "tab", "open"));
  const [isConnected, setIsConnected] = useState(socket.connected);

  // If URL has explicit filters, treat them as applied.
  useEffect(() => {
    const hasAnyUrlFilter =
      Boolean(searchParams.get("search")) ||
      Boolean(searchParams.get("startDate")) ||
      Boolean(searchParams.get("endDate")) ||
      Boolean(searchParams.get("subject"));
    if (hasAnyUrlFilter) setLastAction("apply");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {

    const senderData = JSON.parse(localStorage.getItem("astroVani-admin-data"));
    const senderId = senderData?._id;

    function onConnect() {
      setIsConnected(true);
      // console.log("Socket connected!");
      if (senderId) {
        socket.emit("new user", senderId);

      } else {

      }
    }

    function onDisconnect() {
      setIsConnected(false);
      // console.log(" Socket disconnected!");
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    if (socket.connected) {
      onConnect();
    }


    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, []);



  useEffect(() => {
    const handleNewTicket = (newTicketData) => {
      if (activeTab === "open") {
        toast.success("A new ticket has arrived! Updating list...");
        const { page, pageSize } = paginationModel;
        let limit = Number(pageSize);
        if (isNaN(limit)) {
          limit = 10000;
        }
        const params = {
          page: page + 1,
          limit,
          status: "active",
          userType: "astro",
        };
        if (appliedSearchQuery && appliedSearchQuery.trim()) {
          params.search = appliedSearchQuery;
        }
        if (appliedStartDate) {
          params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
        }
        if (appliedEndDate) {
          params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
        }
        if (appliedSubject && appliedSubject.trim()) {
          params.subject = appliedSubject;
        }
        dispatch(fetchAstroTickets(params));
      }
    };

    socket.on('new ticket', handleNewTicket);
    return () => {
      socket.off('new ticket', handleNewTicket);
    };
  }, [dispatch, activeTab, paginationModel, appliedSearchQuery, appliedStartDate, appliedEndDate, appliedSubject]);

  // Apply Filters Handler - Applies search and date filters together
  const handleApplyFilters = () => {
    setIsApplyingFilters(true);
    applySawLoadingRef.current = false;
    setAppliedSearchQuery(searchQuery.trim());
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedSubject(subject || '');
    setLastAction("apply");
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Apply button should stay disabled until request completes.
  useEffect(() => {
    if (!isApplyingFilters) return;
    if (ticketsLoading) {
      applySawLoadingRef.current = true;
      return;
    }
    if (!ticketsLoading && applySawLoadingRef.current) {
      applySawLoadingRef.current = false;
      setIsApplyingFilters(false);
    }
  }, [ticketsLoading, isApplyingFilters]);

  // Keep URL in sync so Back restores pagination/filters.
  useEffect(() => {
    if (!didInitUrlSyncRef.current) {
      didInitUrlSyncRef.current = true;
      return;
    }
    const params = new URLSearchParams();
    params.set("tab", activeTab);
    params.set("page", String(paginationModel.page));
    params.set("pageSize", String(paginationModel.pageSize));
    setIfTruthy(params, "search", appliedSearchQuery);
    setIfTruthy(params, "startDate", appliedStartDate);
    setIfTruthy(params, "endDate", appliedEndDate);
    setIfTruthy(params, "subject", appliedSubject);
    setSearchParams(params, { replace: true });
  }, [
    activeTab,
    paginationModel.page,
    paginationModel.pageSize,
    appliedSearchQuery,
    appliedStartDate,
    appliedEndDate,
    appliedSubject,
    setSearchParams,
  ]);

  useEffect(() => {
    const { page, pageSize } = paginationModel;
    const status = activeTab === "open" ? "active" : "close";
    let limit = Number(pageSize);
    if (isNaN(limit)) {
      limit = 10000; // Use fixed default
    }

    const params = {
      page: page + 1,
      limit,
      status,
      userType: "astro",
    };

    // Date logic: Only send dates if explicitly applied
    if (appliedStartDate && appliedEndDate) {
      // Dates were explicitly applied - use them
      params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
      params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
    }
    // If no dates applied, don't send dates

    // Add search parameters if search query exists
    if (appliedSearchQuery && appliedSearchQuery.trim()) {
      params.search = appliedSearchQuery;
    }

    // Add subject filter if subject is selected
    if (appliedSubject && appliedSubject.trim()) {
      params.subject = appliedSubject;
    }

    // Prevent duplicate API calls by comparing params
    const paramsString = JSON.stringify(params);
    if (lastParamsRef.current === paramsString && !isInitialMount.current) {
      return; // Skip if params haven't changed
    }

    lastParamsRef.current = paramsString;
    isInitialMount.current = false;

    dispatch(fetchAstroTickets(params));
  }, [paginationModel, dispatch, activeTab, appliedSearchQuery, appliedStartDate, appliedEndDate, appliedSubject, lastAction, todayDate]);

  // Open delete confirmation dialog
  const handleClickOpen = (feedbackId) => {
    setSelectedFeedbackId(feedbackId);
    setOpen(true);
  };

  // Close delete dialog
  const handleClose = () => {
    setOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPaginationModel({ ...paginationModel, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const newPageSize = Number(event.target.value);
    setPaginationModel({ page: 0, pageSize: newPageSize });
  };

  const handleResetFilter = () => {
    setSearchQuery('');
    setAppliedSearchQuery('');
    setStartDate("");
    setEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setSubject("");
    setAppliedSubject("");
    setLastAction(null);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const status = activeTab === "open" ? "active" : "close";
      const params = new URLSearchParams({
        status,
        userType: "astro",
      });

      // Send dates only if explicitly provided
      if (appliedStartDate) {
        params.append('startDate', convertISTToUTCForAPI(appliedStartDate, 'start'));
      }
      if (appliedEndDate) {
        params.append('endDate', convertISTToUTCForAPI(appliedEndDate, 'end'));
      }

      // Include search parameters in export if search was applied
      if (appliedSearchQuery && appliedSearchQuery.trim()) {
        params.append('search', appliedSearchQuery);
      }

      // Include subject filter in export if subject was applied
      if (appliedSubject && appliedSubject.trim()) {
        params.append('subject', appliedSubject);
      }

      const url = `${process.env.REACT_APP_BASEURL}/admin/ticket?${params.toString()}`;
      const response = await axios.get(url, {
        headers: { Authorization: token },
      });

      const allData = response?.data?.data || [];
      const exportData = allData.map((item, index) => ({
        'S.No': index + 1,
        'Ticket Number': item?.ticketId || '-',
        'User Name': item?.fullName || '-',
        'Email': item?.email || '-',
        'Phone Number': item?.mobile || '-',
        'Feedback': item?.description || '-',
        'Subject': item?.subject || '-',
        'Status': item?.status === "active" ? "Open" : "Closed",
        'Created Date': formatUTCDate(item?.createdAt, { format: 'date', separator: '/' }),
        'Created Time': formatUTCDate(item?.createdAt, { format: 'time', showSeconds: true }),
        'Updated Date': formatUTCDate(item?.updatedAt, { format: 'date', separator: '/' }),
        'Updated Time': formatUTCDate(item?.updatedAt, { format: 'time', showSeconds: true }),
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Astrologer Tickets');
      XLSX.writeFile(wb, `Astrologer_Tickets_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Export successful!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDelete = async () => {
    if (selectedFeedbackId) {
      await dispatch(deleteAstroTicket(selectedFeedbackId));
      const { page, pageSize } = paginationModel;
      const status = activeTab === "open" ? "active" : "close";
      let limit = Number(pageSize);
      if (isNaN(limit)) {
        limit = 10;
      }
      dispatch(fetchAstroTickets({
        page: page + 1,
        limit,
        status,
        userType: "astro",
        search: appliedSearchQuery || undefined,
        startDate: appliedStartDate ? convertISTToUTCForAPI(appliedStartDate, 'start') : undefined,
        endDate: appliedEndDate ? convertISTToUTCForAPI(appliedEndDate, 'end') : undefined,
        subject: appliedSubject || undefined,
      }));
    }
    setOpen(false);
  };

  const filteredData = useMemo(() => {
    return DataAstro?.filter((feedback) => {
      if (activeTab === "open") {
        return feedback?.status === "active";
      } else if (activeTab === "close") {
        return feedback?.status === "close";
      }
      return false;
    }) || [];
  }, [DataAstro, activeTab]);

  const columns = [
    {
      field: "sono",
      headerName: "S.No",
      width: 80,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <IconButton
            size="small"
            component={Link}
            to={`/AstroSupportChat/${row.id}`}
            title="View Ticket"
            sx={{
              color: '#d32f2f',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
              },
            }}
          >
            <i className="icofont-eye" style={{ fontSize: '18px' }}></i>
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleClickOpen(row.id)}
            title="Delete Ticket"
            sx={{
              color: '#d32f2f',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
              },
            }}
          >
            <i className="icofont-ui-delete" style={{ fontSize: '18px' }}></i>
          </IconButton>
        </Box>
      ),
    },
    { field: "ticketId", headerName: "Ticket Number", width: 150 },
    { field: "userName", headerName: "User Name", width: 200 },
    { field: "email", headerName: "Email", width: 250 },
    { field: "phoneNumber", headerName: "Phone Number", width: 150 },
    { field: "subject", headerName: "Subject", width: 200 },
    {
      field: "feedback",
      headerName: "Feedback",
      width: 300,
      renderCell: ({ row }) => (
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
          title={row?.feedback || ''}
        >
          {row?.feedback || '-'}
        </Box>
      ),
    },
    { field: 'createdAt', headerName: 'Created At', width: 200, type: 'dateTime' },
    { field: 'updatedAt', headerName: 'Updated At', width: 200, type: 'dateTime' },
  ];

  const rows = useMemo(() => {
    return filteredData?.map((feedback, index) => ({
      id: feedback?._id || index.toString(),
      sono: paginationModel.page * paginationModel.pageSize + index + 1,
      userName: feedback?.fullName || "N/A",
      email: feedback?.email || "N/A",
      phoneNumber: feedback?.mobile || "N/A",
      subject: feedback?.subject || '-',
      feedback: feedback?.description || "No feedback provided",
      ticketId: feedback?.ticketId || "N/A",
      status: feedback?.status === "active" ? "Open" : "Closed",
      createdAt: feedback?.createdAt ? new Date(feedback?.createdAt) : null,
      updatedAt: feedback?.updatedAt ? new Date(feedback?.updatedAt) : null,
    })) || [];
  }, [filteredData, paginationModel]);

  return (
    <>
      <ToastContainer />
      <div className="body d-flex">
        <div className="container-xxl">
          <PageHeader1 pagetitle="Astrologer Ticket List" />

          {/* Buttons Card */}
          <div className="card mb-3" style={{
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body" style={{ padding: '16px 20px' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant={activeTab === "open" ? "contained" : "outlined"}
                  onClick={() => {
                    setActiveTab("open");
                    setPaginationModel(prev => ({ ...prev, page: 0 }));
                    setSearchQuery('');
                    setAppliedSearchQuery('');
                    setStartDate("");
                    setEndDate("");
                    setAppliedStartDate("");
                    setAppliedEndDate("");
                    setSubject("");
                    setAppliedSubject("");
                    setLastAction(null);
                  }}
                  sx={{
                    fontSize: '14px',
                    minHeight: 30,
                    padding: '4px 16px',
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: '8px',
                    backgroundColor: activeTab === "open"
                      ? 'var(--primary-color, #E7B242)'
                      : 'transparent',
                    color: activeTab === "open"
                      ? '#ffffff'
                      : 'rgba(0, 0, 0, 0.6)',
                    borderColor: activeTab === "open"
                      ? 'var(--primary-color, #E7B242)'
                      : 'rgba(0, 0, 0, 0.23)',
                    '&:hover': {
                      backgroundColor: activeTab === "open"
                        ? 'var(--primary-color, #E7B242)'
                        : 'rgba(0, 0, 0, 0.04)',
                      color: activeTab === "open"
                        ? '#ffffff'
                        : 'var(--primary-color, #E7B242)',
                      borderColor: activeTab === "open"
                        ? 'var(--primary-color, #E7B242)'
                        : 'var(--primary-color, #E7B242)',
                    },
                    boxShadow: activeTab === "open" ? 'none' : 'none',
                  }}
                >
                  Open Tickets
                </Button>
                <Button
                  variant={activeTab === "close" ? "contained" : "outlined"}
                  onClick={() => {
                    setActiveTab("close");
                    setPaginationModel(prev => ({ ...prev, page: 0 }));
                    setSearchQuery('');
                    setAppliedSearchQuery('');
                    setStartDate("");
                    setEndDate("");
                    setAppliedStartDate("");
                    setAppliedEndDate("");
                    setSubject("");
                    setAppliedSubject("");
                    setLastAction(null);
                  }}
                  sx={{
                    fontSize: '14px',
                    minHeight: 30,
                    padding: '4px 16px',
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: '8px',
                    backgroundColor: activeTab === "close"
                      ? 'var(--primary-color, #E7B242)'
                      : 'transparent',
                    color: activeTab === "close"
                      ? '#ffffff'
                      : 'rgba(0, 0, 0, 0.6)',
                    borderColor: activeTab === "close"
                      ? 'var(--primary-color, #E7B242)'
                      : 'rgba(0, 0, 0, 0.23)',
                    '&:hover': {
                      backgroundColor: activeTab === "close"
                        ? 'var(--primary-color, #E7B242)'
                        : 'rgba(0, 0, 0, 0.04)',
                      color: activeTab === "close"
                        ? '#ffffff'
                        : 'var(--primary-color, #E7B242)',
                      borderColor: activeTab === "close"
                        ? 'var(--primary-color, #E7B242)'
                        : 'var(--primary-color, #E7B242)',
                    },
                    boxShadow: activeTab === "close" ? 'none' : 'none',
                  }}
                >
                  Closed Tickets
                </Button>
              </Box>

              {/* Search and Filter Toolbar - Single Line */}
              <Box sx={{ width: '100%', mt: 2 }}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <TextField
                    placeholder="Search..."
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') { handleApplyFilters(); } }}
                    sx={{
                      width: { xs: '120px', sm: '150px', md: '180px' },
                      flexShrink: 0,
                      background: 'var(--card-color)',
                      borderRadius: '9px',
                      '& .MuiInputBase-input': { fontSize: '13px', padding: '9px 12px' },
                    }}
                  />
                  <TextField
                    label="Start Date"
                    type="date"
                    variant="outlined"
                    size="small"
                    value={startDate || ""}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      width: { xs: '140px', sm: '160px', md: '180px' },
                      flexShrink: 0,
                      '& .MuiInputBase-input[type="date"]': {
                        paddingRight: '8px',
                        paddingLeft: '14px',
                        position: 'relative',
                        '&::-webkit-calendar-picker-indicator': {
                          cursor: 'pointer',
                          padding: '2px',
                          marginLeft: '0px',
                          marginRight: '4px',
                          opacity: 0.7,
                          position: 'absolute',
                          right: 0,
                          '&:hover': {
                            opacity: 1,
                          },
                        },
                      },
                      '& .MuiOutlinedInput-root': {
                        paddingRight: '8px',
                      },
                    }}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    variant="outlined"
                    size="small"
                    value={endDate || ""}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      width: { xs: '140px', sm: '160px', md: '180px' },
                      flexShrink: 0,
                      '& .MuiInputBase-input[type="date"]': {
                        paddingRight: '8px',
                        paddingLeft: '14px',
                        position: 'relative',
                        '&::-webkit-calendar-picker-indicator': {
                          cursor: 'pointer',
                          padding: '2px',
                          marginLeft: '0px',
                          marginRight: '4px',
                          opacity: 0.7,
                          position: 'absolute',
                          right: 0,
                          '&:hover': {
                            opacity: 1,
                          },
                        },
                      },
                      '& .MuiOutlinedInput-root': {
                        paddingRight: '8px',
                        },
                      }}
                    />
                    <FormControl
                      variant="outlined"
                      size="small"
                      sx={{
                        width: { xs: '140px', sm: '180px', md: '200px' },
                        flexShrink: 0,
                      }}
                    >
                      <InputLabel id="subject-select-label">Subject</InputLabel>
                      <Select
                        labelId="subject-select-label"
                        id="subject-select"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        label="Subject"
                        sx={{
                          '& .MuiSelect-select': {
                            fontSize: '13px',
                            padding: '9px 12px',
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {subjectOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      onClick={handleApplyFilters}
                      disabled={ticketsLoading || isApplyingFilters || (!searchQuery.trim() && !startDate && !endDate && !subject)}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      backgroundColor: "var(--primary-color, #E7B242)",
                      color: "#fff",
                      height: 40,
                      px: 2,
                      flexShrink: 0,
                      "&:hover": { backgroundColor: "var(--primary-color, #E7B242)", opacity: 0.9 },
                      "&:disabled": {
                        backgroundColor: "rgba(0, 0, 0, 0.12)",
                        color: "rgba(0, 0, 0, 0.26)",
                      },
                    }}
                  >
                    {isApplyingFilters ? "Applying..." : "Apply Filters"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetFilter}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      borderColor: 'var(--primary-color, #E7B242)',
                      color: 'var(--primary-color, #E7B242)',
                      height: 40,
                      px: 2,
                      '&:hover': { borderColor: 'var(--primary-color, #E7B242)', backgroundColor: 'rgba(231, 178, 66, 0.08)' },
                      flexShrink: 0
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    onClick={exportToExcel}
                    disabled={isExporting}
                    sx={{
                      backgroundColor: 'var(--primary-color, #E7B242)',
                      '&:hover': {
                        backgroundColor: 'var(--primary-color, #E7B242)',
                        opacity: 0.9,
                      },
                      '&:disabled': {
                        backgroundColor: 'var(--primary-color, #E7B242)',
                        opacity: 0.6,
                      },
                      borderRadius: '8px',
                      textTransform: 'none',
                      height: 40,
                      px: 2,
                      flexShrink: 0,
                    }}
                  >
                    {isExporting ? 'Exporting...' : 'Export'}
                  </Button>
                </Box>
              </Box>
            </div>
          </div>

          <div className="row g-0 mb-3">
            <div className="col-md-12">
              <div className="card" style={{
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: 'none',
                overflow: 'hidden'
              }}>
                <div className="card-body" style={{ padding: 0 }}>
                  <Box sx={{
                    width: '100%',
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid #f0f0f0',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f8f9fa',
                      borderBottom: '2px solid #e9ecef',
                      fontWeight: 600,
                    },
                    '& .MuiDataGrid-footerContainer': {
                      display: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#f8f9fa',
                    },
                  }}>
                    <DataGrid
                      columns={columns}
                      rows={rows}
                      loading={ticketsLoading}
                      disableSelectionOnClick
                      paginationMode="server"
                      hideFooterPagination
                      autoHeight
                      getRowId={(row) => row.id}
                      columnVisibilityModel={{}}
                      sx={{
                        '& .MuiDataGrid-cell:focus': {
                          outline: 'none',
                        },
                        '& .MuiDataGrid-cell:focus-within': {
                          outline: 'none',
                        },
                      }}
                    />
                    <TablePagination
                      component="div"
                      count={pagination?.totalDocs || 0}
                      page={paginationModel.page}
                      onPageChange={handleChangePage}
                      rowsPerPage={paginationModel.pageSize}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[10, 25, 50]}
                    />
                  </Box>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this Astrologer Support ticket?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
}

export default AstrologerSupportList;